import React, { useState, useEffect } from 'react';
import { Row, Col, Form, Button, Table, Modal, Badge } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../shared/api';
import { useConfirm } from '../../shared/ConfirmContext';
import PageShell from '../../components/layout/PageShell';
import PageSection from '../../components/layout/PageSection';
import AppButton from '../../components/ui/AppButton';
import { CenteredLoader } from '../../components/LoadingSpinner';
import DimensionUnitsSection, { DIMENSION_UNITS_QUERY_KEY, fetchDimensionUnits } from './components/DimensionUnitsSection';

const EMPTY_UNIT = { name: '', symbol: '', factor_to_g: '', is_active: true, order: 0 };

const GeneralSettings = () => {
  const { t } = useTranslation();
  const confirm = useConfirm();
  const queryClient = useQueryClient();

  const [settingsId, setSettingsId] = useState(null);
  const [formData, setFormData] = useState({
    date_format: 'DD/MM/YYYY',
    time_format: '24H',
    weight_unit_id: null,
    dimension_unit_id: null,
  });
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const [allWeightUnits, setAllWeightUnits] = useState([]);
  const [activeWeightUnits, setActiveWeightUnits] = useState([]);
  const [showUnitModal, setShowUnitModal] = useState(false);
  const [editingUnit, setEditingUnit] = useState(null);
  const [unitForm, setUnitForm] = useState(EMPTY_UNIT);
  const [unitError, setUnitError] = useState(null);

  const [convValue, setConvValue] = useState('');
  const [convFrom, setConvFrom] = useState('');
  const [convTo, setConvTo] = useState('');
  const [convResult, setConvResult] = useState(null);
  const [convLoading, setConvLoading] = useState(false);

  const { isPending: loading, data: queryData } = useQuery({
    queryKey: ['general-settings'],
    queryFn: async () => {
      const [sysRes, allUnitsRes] = await Promise.all([
        api.get('/settings/api/system/'),
        api.get('settings/api/weight-units/'),
      ]);
      const data = Array.isArray(sysRes) ? sysRes[0] : sysRes;
      const units = Array.isArray(allUnitsRes) ? allUnitsRes : (allUnitsRes.results || []);
      return { data, units };
    },
  });

  const { data: dimensionUnitsData } = useQuery({
    queryKey: DIMENSION_UNITS_QUERY_KEY,
    queryFn: fetchDimensionUnits,
  });
  const activeDimensionUnits = (dimensionUnitsData || []).filter(u => u.is_active);

  useEffect(() => {
    if (!dimensionUnitsData) return;
    const defaultUnit = dimensionUnitsData.find(u => u.is_default);
    const fallback = dimensionUnitsData.find(u => u.is_active);
    setFormData(prev => ({ ...prev, dimension_unit_id: defaultUnit ? defaultUnit.id : (fallback?.id ?? null) }));
  }, [dimensionUnitsData]);

  useEffect(() => {
    if (!queryData) return;
    const { data, units } = queryData;
    const active = units.filter(u => u.is_active);
    const defaultUnit = units.find(u => u.is_default);
    setAllWeightUnits(units);
    setActiveWeightUnits(active);
    if (data) {
      setSettingsId(data.id);
      setFormData(prev => ({
        ...prev,
        date_format: data.date_format || 'DD/MM/YYYY',
        time_format: data.time_format || '24H',
        weight_unit_id: defaultUnit ? defaultUnit.id : (active[0]?.id ?? null),
      }));
    }
    if (!convFrom && active.length) setConvFrom(String(active[0].id));
    if (!convTo && active.length > 1) setConvTo(String(active[1].id));
  }, [queryData]);

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['general-settings'] });

  const saveMutation = useMutation({
    mutationFn: async (payload) => {
      const sysPayload = {
        date_format: payload.date_format,
        time_format: payload.time_format,
      };
      const saveReq = settingsId
        ? api.patch(`/settings/api/system/${settingsId}/`, sysPayload)
        : api.post('/settings/api/system/', sysPayload);
      await saveReq;
      if (payload.weight_unit_id) {
        await api.post(`settings/api/weight-units/${payload.weight_unit_id}/set_default/`);
      }
      if (payload.dimension_unit_id) {
        await api.post(`settings/api/dimension-units/${payload.dimension_unit_id}/set_default/`);
      }
    },
    onSuccess: () => {
      setSuccess(t('settings.general.success.updated'));
      queryClient.invalidateQueries({ queryKey: DIMENSION_UNITS_QUERY_KEY });
    },
    onError: () => setError(t('settings.general.errors.saveFailed')),
  });
  const saving = saveMutation.isPending;

  const unitSaveMutation = useMutation({
    mutationFn: (payload) => editingUnit
      ? api.patch(`settings/api/weight-units/${editingUnit.id}/`, payload)
      : api.post('settings/api/weight-units/', payload),
    onSuccess: () => { setShowUnitModal(false); invalidate(); },
    onError: (err) => setUnitError(err.message || t('settings.general.errors.saveFailed')),
  });
  const unitSaving = unitSaveMutation.isPending;

  const unitDeleteMutation = useMutation({
    mutationFn: (id) => api.delete(`settings/api/weight-units/${id}/`),
    onSuccess: invalidate,
    onError: (err) => setError(err.message),
  });
  const unitsLoading = unitDeleteMutation.isPending;

  const unitDefaultMutation = useMutation({
    mutationFn: (id) => api.post(`settings/api/weight-units/${id}/set_default/`),
    onSuccess: (_, id) => {
      setFormData(prev => ({ ...prev, weight_unit_id: id }));
      invalidate();
    },
    onError: (err) => setError(err.message),
  });
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSave = (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);
    saveMutation.mutate(formData);
  };

  const openAddUnit = () => {
    setEditingUnit(null);
    setUnitForm(EMPTY_UNIT);
    setUnitError(null);
    setShowUnitModal(true);
  };

  const openEditUnit = (unit) => {
    setEditingUnit(unit);
    setUnitForm({ name: unit.name, symbol: unit.symbol, factor_to_g: unit.factor_to_g, is_active: unit.is_active, order: unit.order });
    setUnitError(null);
    setShowUnitModal(true);
  };

  const handleUnitSubmit = (e) => {
    e.preventDefault();
    setUnitError(null);
    const payload = { ...unitForm, factor_to_g: parseFloat(unitForm.factor_to_g), order: parseInt(unitForm.order, 10) || 0 };
    unitSaveMutation.mutate(payload);
  };

  const handleDeleteUnit = async (unit) => {
    if (!await confirm(t('settings.weightUnits.deleteConfirm'))) return;
    unitDeleteMutation.mutate(unit.id);
  };

  const handleSetDefault = (unit) => {
    unitDefaultMutation.mutate(unit.id);
  };

  // ── Converter ─────────────────────────────────────────────────────────────
  const handleConvert = async () => {
    if (!convValue || !convFrom || !convTo) return;
    try {
      setConvLoading(true);
      const res = await api.post('settings/api/weight-units/convert/', {
        value: parseFloat(convValue),
        from_unit: convFrom,
        to_unit: convTo,
      });
      setConvResult(res);
    } catch (err) {
      setConvResult({ error: err.message });
    } finally {
      setConvLoading(false);
    }
  };

  if (loading) {
    return (
      <PageShell pretitle={t('menu.settings')} title={t('settings.general.title')}>
        <CenteredLoader />
      </PageShell>
    );
  }

  return (
    <PageShell
      pretitle={t('menu.settings')}
      title={t('settings.general.title')}
      actions={
        <AppButton variant="primary" onClick={handleSave} loading={saving} loadingText={t('settings.general.saving')}>
          {t('settings.general.save')}
        </AppButton>
      }
    >
      {error && <div className="alert alert-danger app-inline-alert" role="alert">{error}</div>}
      {success && <div className="alert alert-success app-inline-alert" role="alert">{success}</div>}

      {/* ── Display & Measurement settings ── */}
      <Form onSubmit={handleSave}>
        <Row className="row-deck row-cards mb-3">
          <Col md={6}>
            <PageSection title={t('settings.general.displaySettings')}>
                    <Form.Group className="mb-3">
                      <Form.Label>{t('settings.general.dateFormat')}</Form.Label>
                      <Form.Select name="date_format" value={formData.date_format} onChange={handleChange}>
                        <option value="DD/MM/YYYY">DD/MM/YYYY</option>
                        <option value="MM/DD/YYYY">MM/DD/YYYY</option>
                        <option value="YYYY-MM-DD">YYYY-MM-DD</option>
                        <option value="DD.MM.YYYY">DD.MM.YYYY</option>
                      </Form.Select>
                    </Form.Group>
                    <Form.Group>
                      <Form.Label>{t('settings.general.timeFormat')}</Form.Label>
                      <Form.Select name="time_format" value={formData.time_format} onChange={handleChange}>
                        <option value="24H">{t('settings.general.timeFormat24')}</option>
                        <option value="12H">{t('settings.general.timeFormat12')}</option>
                      </Form.Select>
                    </Form.Group>
            </PageSection>
          </Col>

          <Col md={6}>
            <PageSection title={t('settings.general.measurementUnits')}>
                    <Form.Group className="mb-3">
                      <Form.Label>{t('settings.general.weightUnit')}</Form.Label>
                      <Form.Select
                        name="weight_unit_id"
                        value={formData.weight_unit_id ?? ''}
                        onChange={e => setFormData(prev => ({ ...prev, weight_unit_id: Number(e.target.value) }))}
                      >
                        {activeWeightUnits.length === 0 && <option value="">—</option>}
                        {activeWeightUnits.map(u => (
                          <option key={u.id} value={u.id}>{u.symbol} – {u.name}</option>
                        ))}
                      </Form.Select>
                      <Form.Text className="text-muted">{t('settings.weightUnits.defaultHelp')}</Form.Text>
                    </Form.Group>
                    <Form.Group>
                      <Form.Label>{t('settings.general.dimensionUnit')}</Form.Label>
                      <Form.Select
                        name="dimension_unit_id"
                        value={formData.dimension_unit_id ?? ''}
                        onChange={e => setFormData(prev => ({ ...prev, dimension_unit_id: Number(e.target.value) }))}
                      >
                        {activeDimensionUnits.length === 0 && <option value="">—</option>}
                        {activeDimensionUnits.map(u => (
                          <option key={u.id} value={u.id}>{u.symbol} – {u.name}</option>
                        ))}
                      </Form.Select>
                      <Form.Text className="text-muted">{t('settings.dimensionUnits.defaultHelp')}</Form.Text>
                    </Form.Group>
            </PageSection>
          </Col>
        </Row>
      </Form>

      {/* ── Weight units management table ── */}
      <Row className="row-deck row-cards mb-3">
        <Col>
          <PageSection
            title={t('settings.weightUnits.title')}
            bodyClassName="p-0"
            actions={
              <Button size="sm" variant="primary" onClick={openAddUnit}>
                <i className="ti ti-plus me-1"></i>
                {t('settings.weightUnits.add')}
              </Button>
            }
          >
                  <Table responsive hover className="mb-0">
                    <thead>
                      <tr>
                        <th>{t('settings.weightUnits.name')}</th>
                        <th>{t('settings.weightUnits.symbol')}</th>
                        <th>{t('settings.weightUnits.factorToG')}</th>
                        <th>{t('settings.weightUnits.order')}</th>
                        <th>{t('settings.currency.active')}</th>
                        <th className="text-end">{t('common.actions')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {allWeightUnits.length === 0 && (
                        <tr><td colSpan={6} className="text-center text-muted py-3">{t('settings.weightUnits.noUnits')}</td></tr>
                      )}
                      {allWeightUnits.map(unit => (
                        <tr key={unit.id}>
                          <td>
                            <strong>{unit.name}</strong>
                            {unit.is_default && (
                              <Badge bg="primary" className="ms-2">{t('settings.currency.default')}</Badge>
                            )}
                          </td>
                          <td><code>{unit.symbol}</code></td>
                          <td>{unit.factor_to_g.toLocaleString()}</td>
                          <td>{unit.order}</td>
                          <td>
                            {unit.is_active
                              ? <span className="badge bg-success-lt">{t('settings.currency.active')}</span>
                              : <span className="badge bg-secondary-lt">{t('settings.currency.inactive')}</span>}
                          </td>
                          <td className="text-end">
                            <div className="btn-group">
                              {!unit.is_default && unit.is_active && (
                                <Button size="sm" variant="outline-primary" onClick={() => handleSetDefault(unit)} disabled={unitsLoading}>
                                  {t('settings.currency.setAsDefault')}
                                </Button>
                              )}
                              <Button size="sm" variant="outline-info" onClick={() => openEditUnit(unit)} title={t('common.edit')}>
                                <i className="ti ti-edit"></i>
                              </Button>
                              {!unit.is_default && (
                                <Button size="sm" variant="outline-danger" onClick={() => handleDeleteUnit(unit)} disabled={unitsLoading} title={t('common.delete')}>
                                  <i className="ti ti-trash"></i>
                                </Button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
          </PageSection>
        </Col>
      </Row>

      {/* ── Weight converter ── */}
      <Row className="row-deck row-cards mb-3">
        <Col md={6}>
          <PageSection title={t('settings.weightUnits.converter')}>
                  <Row className="g-2 align-items-end">
                    <Col>
                      <Form.Label>{t('settings.weightUnits.value')}</Form.Label>
                      <Form.Control
                        type="number"
                        value={convValue}
                        onChange={e => { setConvValue(e.target.value); setConvResult(null); }}
                        placeholder="0"
                      />
                    </Col>
                    <Col>
                      <Form.Label>{t('settings.weightUnits.from')}</Form.Label>
                      <Form.Select value={convFrom} onChange={e => { setConvFrom(e.target.value); setConvResult(null); }}>
                        {activeWeightUnits.map(u => <option key={u.id} value={u.id}>{u.symbol}</option>)}
                      </Form.Select>
                    </Col>
                    <Col>
                      <Form.Label>{t('settings.weightUnits.to')}</Form.Label>
                      <Form.Select value={convTo} onChange={e => { setConvTo(e.target.value); setConvResult(null); }}>
                        {activeWeightUnits.map(u => <option key={u.id} value={u.id}>{u.symbol}</option>)}
                      </Form.Select>
                    </Col>
                    <Col xs="auto">
                      <Button variant="secondary" onClick={handleConvert} disabled={convLoading || !convValue}>
                        {convLoading ? <span className="spinner-border spinner-border-sm" /> : '='}
                      </Button>
                    </Col>
                  </Row>
          {convResult && !convResult.error && (
            <div className="alert alert-info mt-3 mb-0">
              <strong>{convResult.formula}</strong>
            </div>
          )}
          {convResult?.error && (
            <div className="alert alert-danger mt-3 mb-0">{convResult.error}</div>
          )}
          </PageSection>
        </Col>
      </Row>

      <DimensionUnitsSection />

      {/* ── Add / Edit unit modal ── */}
      <Modal show={showUnitModal} onHide={() => setShowUnitModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>
            {editingUnit ? t('settings.weightUnits.edit') : t('settings.weightUnits.add')}
          </Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleUnitSubmit}>
          <Modal.Body>
            {unitError && <Alert variant="danger">{unitError}</Alert>}
            <Form.Group className="mb-3">
              <Form.Label>{t('settings.weightUnits.name')} *</Form.Label>
              <Form.Control
                type="text"
                value={unitForm.name}
                onChange={e => setUnitForm(p => ({ ...p, name: e.target.value }))}
                placeholder={t('settings.weightUnits.namePlaceholder')}
                required
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>{t('settings.weightUnits.symbol')} *</Form.Label>
              <Form.Control
                type="text"
                value={unitForm.symbol}
                onChange={e => setUnitForm(p => ({ ...p, symbol: e.target.value }))}
                placeholder="kg"
                maxLength={10}
                required
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>{t('settings.weightUnits.factorToG')} *</Form.Label>
              <Form.Control
                type="number"
                step="any"
                min="0.000001"
                value={unitForm.factor_to_g}
                onChange={e => setUnitForm(p => ({ ...p, factor_to_g: e.target.value }))}
                placeholder="1000"
                required
              />
              <Form.Text className="text-muted">{t('settings.weightUnits.factorHelp')}</Form.Text>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>{t('settings.weightUnits.order')}</Form.Label>
              <Form.Control
                type="number"
                min="0"
                value={unitForm.order}
                onChange={e => setUnitForm(p => ({ ...p, order: e.target.value }))}
              />
            </Form.Group>
            <Form.Check
              type="switch"
              id="unit-active"
              label={t('settings.currency.active')}
              checked={unitForm.is_active}
              onChange={e => setUnitForm(p => ({ ...p, is_active: e.target.checked }))}
            />
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowUnitModal(false)}>{t('common.cancel')}</Button>
            <AppButton variant="primary" type="submit" loading={unitSaving} loadingText={t('common.saving')}>
              {t('common.save')}
            </AppButton>
          </Modal.Footer>
        </Form>
      </Modal>
    </PageShell>
  );
};

export default GeneralSettings;
